<template>
  <template v-if="type === ICON_TYPE.FA">
    <app-icon-ds
      :color="iconStyle.color"
      :icon="name"
      :size="iconStyle.fontSize"
      :weight="iconStyle.fontWeight"
    />
  </template>
  <template v-else>
    <span
      class="material-symbols-outlined cursor-pointer"
      :style="iconStyle"
    >
      {{ name }}
    </span>
  </template>
</template>

<script lang="ts">
import { defineComponent, computed, PropType } from 'vue'

const ICON_TYPE = {
  FA: 'fa',
  MATERIAL: 'material',
} as const

const APP_ICON_SIZES = {
  'xxxxs': 8,
  'xxxs': 10,
  'xxs': 12,
  'xs': 16,
  'sm': 20,
  'md': 24,
  'lg': 28,
  'xl': 32,
  'xxl': 48,
} as const

type AppIconSize = keyof typeof APP_ICON_SIZES
type AppIconType = typeof ICON_TYPE[keyof typeof ICON_TYPE]
type AppIconWeight = 'fak' | 'far' | 'fas'

/**
 * AppMaterialIcon
 * Wrapper component for using Material Symbols icons
 * with customizable font variation settings.
 * @see https://fonts.google.com/icons — Browse all available icon names
 */
export default defineComponent({
  name: 'AppMaterialIcon',
  props: {
    /**
     * The name of the Material icon to display.
     * @example "close", "help", "check"
     */
    name: { 
      type: String,
      required: true,
    },
    /**
     * Size of the icon in pixels (default: 20px container).
     */
    size: { 
      type:  [Number, String] as PropType<number | AppIconSize>,
      default: 20,
    },
    /**
     * Whether the icon is filled (1) or outlined (0).
     */
    filled: { type: Boolean },

    /**
     * Weight: from 100 to 700. Default is 400.
     */
    weight: { 
      type: [Number, String] as PropType<number | AppIconWeight>,
      default: 400,
    },
    /**
     * Grade: fine-tuning of thickness (-50 to 200).
     */
    grade: { 
      type: Number,
      default: 0,
    },
    /**
     * Optical size: must match the size for best results (20, 24, 40, 48).
      * Adjusts details for better rendering at different sizes.
     */
    opticalSize: { 
      type: Number,
    },
    /**
     * Color of the icon (any valid CSS color value).
     * @example "#068925" or "red" or "var(--your-variable)"
     */
    color: { 
      type: String,
      default: '#375576',
    },
    /**
     * Type of icon set to use (default: 'material').
     */
    type: { 
      type: String as () => AppIconType,
      default: 'material',
    },
  },
  setup(props) {
    const getFinalSize = () => {
      if (typeof props.size === 'number') {
        return props.size
      }
      return APP_ICON_SIZES[props.size as AppIconSize] || 20
    }
    const finalSize = computed(() => getFinalSize())
    const finalOpticalSize = computed(() => props.opticalSize || finalSize.value)

    const iconStyle = computed(() => {
      if(props.type === ICON_TYPE.FA) {
        const getFontWeight = () => {
          if (props.filled) return 'fas'
          return typeof props.weight === 'number' ? 'far' : props.weight
        }

        return {
          color: props.color === '#375576' ? 'grey-600' : props.color,
          fontSize: typeof props.size === 'number' ? 'md' : props.size,
          fontWeight: getFontWeight(),
        }
      } 

      return {
        color: props.color,
        fontSize: `${finalSize.value}px`, 
      }
    
    })
    return { iconStyle, ICON_TYPE, finalOpticalSize }
  },
})
</script>

<style lang="scss" scoped>
.material-symbols-outlined {
  font-variation-settings: 'FILL' v-bind(Number(filled)), 'wght' v-bind(weight), 'GRAD' v-bind(grade), 'opsz' v-bind(finalOpticalSize);
}
</style>
